import { MoveStageMembersDialog } from 'app/components/lists/board/MoveStageMembersDialog'
import type { BulkProgressReporter } from 'app/components/table/bulkActions/types'
import type { Contact, List, ListStage } from 'app/generated/graphql'
import { moveStageMembers, readStageCounts, readStageMemberIds, readStageRowIds } from 'app/lib/listApi'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore, useListStore } from 'app/stores/StoreContext'
import type { StageType } from 'app/types/entities'
import { recordPlacement } from 'lib/listPlacements'
import { deleteBlockedMessage } from 'lib/listStages'
import { logError } from 'lib/logging'
import {
  defaultStageIdOf,
  isFilterList,
  listStageNameTaken,
  withListStageAdded,
  withListStageDeleted,
  withListStageEdited,
} from 'lib/practiceLists'

// A system board reads its list by id without touching the store, so either copy can be the stale one:
// the store's from an earlier visit, or the opened render's after a stage form stayed open across a refetch.
// The newer copy wins; a tie or a copy that cannot be dated keeps the store's. It is chosen at save time,
// because saving older stages would drop any stage added since.
function newerList(hits: readonly List[], opened: List): List {
  const stored = hits.find((candidate) => candidate.id === opened.id)
  if (!stored) return opened
  if (stored.version !== undefined && opened.version !== undefined && stored.version !== opened.version) {
    return opened.version > stored.version ? opened : stored
  }
  return opened.updatedDate && stored.updatedDate && opened.updatedDate > stored.updatedDate ? opened : stored
}

/**
 * Stage edits for a practice list, addressed by stage id. Same contract `EditPipeline` already
 * speaks, so the existing stage form is reused as is. Members point at a stage id, so a rename
 * rewrites nothing.
 */
export function useListStageEditor({ list: openedList, onChanged }: { list: List; onChanged: () => void }) {
  const listStore = useListStore()
  const contactStore = useContactStore()

  const reportSaveError = (message: string) => {
    logError(`Error saving a list's stage`, message)
    notifyError(`That stage could not be saved. ${message}`, `stage-update-error`)
    closeModal()
  }

  const updateStage = async (
    stage: StageType,
    index: number,
    options: { makeDefault?: boolean } = {},
  ): Promise<void | { error?: string }> => {
    const list = newerList(listStore.hits, openedList)
    const stages = list.stages ?? []
    const existing = index === -1 ? undefined : stages[index]
    if (listStageNameTaken(stages, stage.name, existing?.id)) return { error: `name-duplicated` }
    const edit = { name: stage.name, color: stage.color ?? existing?.color ?? `` }

    if (existing) {
      const result = await listStore.update(list.id, {
        stages: withListStageEdited(stages, existing.id, edit),
        ...(options.makeDefault && { defaultStageId: existing.id }),
      })
      if (result.error) return reportSaveError(result.error.message)
      notifySuccess(`Stage saved`, `stage-update`)
      onChanged()
      closeModal()
      return
    }

    const added = await listStore.update(list.id, { stages: withListStageAdded(stages, edit) })
    if (added.error) return reportSaveError(added.error.message)
    // The server assigns the new stage its id, and it is the only way to name it as the default.
    if (options.makeDefault || stages.length === 0) {
      const knownIds = new Set(stages.map((candidate) => candidate.id))
      // EntityStore types a mutation's data as `{}`, so the response shape is stated here.
      const response = added.data as { updateList?: Pick<List, `stages`> } | undefined
      const created = response?.updateList?.stages?.find((candidate) => !knownIds.has(candidate.id))
      const made = created
        ? await listStore.update(list.id, { defaultStageId: created.id })
        : { error: { message: `The new stage came back without an id.` } }
      if (made.error) {
        // The stage exists even though it is not the default yet, so the board still has to refresh.
        onChanged()
        return reportSaveError(made.error.message)
      }
    }
    notifySuccess(`Stage added`, `stage-update`)
    onChanged()
    closeModal()
  }

  const removeStage = async (stageId: string, nextDefaultId: string | undefined, success: string) => {
    const list = newerList(listStore.hits, openedList)
    const result = await listStore.update(list.id, {
      stages: withListStageDeleted(list.stages ?? [], stageId),
      ...(nextDefaultId && { defaultStageId: nextDefaultId }),
    })
    if (result.error) {
      logError(`Error deleting a list's stage`, result.error.message)
      notifyError(`That stage could not be deleted. ${result.error.message}`, `stage-delete-error`)
    } else {
      notifySuccess(success, `stage-delete`)
      onChanged()
    }
    closeModal()
  }

  const openMoveDialog = (list: List, stage: ListStage, count: number, targets: ListStage[]) => {
    const isDefault = stage.id === defaultStageIdOf(list)
    const onMove = async (targetId: string, onProgress: BulkProgressReporter) => {
      const target = targets.find((candidate) => candidate.id === targetId)!
      let contacts: Contact[]
      let ids: string[]
      try {
        // The default stage is read by its rows alone: the rule matches `listMembers` files under it need
        // no write, they follow whichever stage is the default next.
        ids = isDefault ? await readStageRowIds(list.id, stage.id) : await readStageMemberIds(list.id, stage.id)
        contacts = await contactStore.readByIds(ids)
      } catch (err) {
        logError(`Error reading who is in a list's stage before moving them`, err)
        notifyError(
          `Could not read who is in ${stage.name}. Nothing was moved or deleted.`,
          `stage-delete-check-failed`,
        )
        closeModal()
        return
      }
      const byId = new Map(contacts.map((contact) => [contact.id, contact]))
      const members = ids.map((entityId) => ({ entityId, label: byId.get(entityId)?.name || entityId }))
      const outcomes = await moveStageMembers(list.id, members, targetId, onProgress)
      const at = Date.now()
      for (const outcome of outcomes) {
        const contact = byId.get(outcome.id)
        if (outcome.ok && contact) recordPlacement(list.id, outcome.id, { stage: targetId, item: contact, at })
      }
      if (outcomes.some((outcome) => !outcome.ok)) {
        onChanged()
        return outcomes
      }
      const people = `${count} ${count === 1 ? `person` : `people`}`
      const newDefault = isDefault ? ` ${target.name} is now the default.` : ``
      await removeStage(
        stage.id,
        isDefault ? targetId : undefined,
        `Moved ${people} to ${target.name} and deleted ${stage.name}.${newDefault}`,
      )
      return undefined
    }
    showModal(
      <MoveStageMembersDialog
        stageName={stage.name}
        count={count}
        targets={targets}
        initialTargetId={defaultStageIdOf({ stages: targets, defaultStageId: list.defaultStageId })}
        onMove={onMove}
      />,
      { isFullScreen: false, removePadding: true, useRadix: true, title: `Delete ${stage.name}` },
      false,
    )
  }

  const deleteStage = async (stageId: string) => {
    const list = newerList(listStore.hits, openedList)
    const stages = list.stages ?? []
    const stage = stages.find((candidate) => candidate.id === stageId)
    if (!stage) {
      closeModal()
      return
    }
    // The count is read unfiltered from the server: a board's lane is narrowed by the active
    // location and search, so a stage can read empty there while people are still in it. If it
    // cannot be read, fail closed and delete nothing.
    let count: number
    try {
      count = (await readStageCounts(list.id)).get(stageId) ?? 0
    } catch (err) {
      logError(`Error checking who is in a list's stage before deleting it`, err)
      notifyError(`Could not check who is in ${stage.name}. Nothing was deleted.`, `stage-delete-check-failed`)
      closeModal()
      return
    }
    const isDefault = stageId === defaultStageIdOf(list)
    const remaining = stages.filter((candidate) => candidate.id !== stageId)
    if (count > 0 && remaining.length > 0) {
      openMoveDialog(list, stage, count, remaining)
      return
    }
    // With no stage left, a filter list's rule matches simply have no stage until one is added.
    if (count > 0 && !(isDefault && isFilterList(list))) {
      notifyError(deleteBlockedMessage(stage.name, count), `stage-delete-blocked`)
      closeModal()
      return
    }
    // The server may keep a dangling default, which would strand a filter list's unrowed matches.
    const nextDefault = isDefault
      ? remaining.find((candidate) => candidate.id === defaultStageIdOf({ stages: remaining }))
      : undefined
    await removeStage(
      stageId,
      nextDefault?.id,
      nextDefault ? `Stage deleted. ${nextDefault.name} is now the default.` : `Stage deleted`,
    )
  }

  return { updateStage, deleteStage }
}
